import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { WorkflowStatus } from '@api/projects/interfaces';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { QuestionStatusService } from '@app/pages/questions/question-status.service';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { ProjectService } from '@app/services/project/project.service';
import { WorkflowRestService } from '@app/services/rest/workflow-rest.service';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import {
	IParticipant,
	IParticipantConfig,
	IQuestionnaireWorkflowItem, ITextButtonConfig,
} from '@interfaces/interfaces';
import {
	SpecialQuestionService,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import {
	CommunicationStatus,
	WorkflowItemDirective,
} from '@workflow/directives/workflow-item/workflow-item.directive';
import { WorkflowService } from '@workflow/services/workflow.service';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import {
	InterpolationService,
	TextInterpolation,
} from '@app/services/interpolation/interpolation.service';
import { MenuService } from '@app/services/menu';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import { EWorkflowPathKey } from '@app/pages/participant/workflow/workflow-path-key.enum';
import { TWorkflowViewMode } from '@workflow/components/navigator/interfaces';
import { ModalDirective } from '@app/directives/modal/modal.directive';

@Component({
	template: '',
	styleUrls: [],
})
export abstract class AbstractQuestionViewComponent implements OnInit, OnDestroy {

	protected abstract viewMode: TWorkflowViewMode;

	protected commonSubscription = new Subscription();

	isLastPageOfQuestionGroup: boolean;
	isPageSufficientFilled: boolean;
	isValidInput: boolean[][] = [];
	isAllValid = true;
	participant: IParticipant;
	status: string;
	unfinishedItems: { items: string[], additionalAmount: number } = { items: [], additionalAmount: 0 };

	workflowItem: IQuestionnaireWorkflowItem;

	individuelleFragetypen: string[];
	alleFrageTypen: string[];

	public finishedAndLocked: boolean;
	public confirmationModalConfig: ITextButtonConfig = null;
	@ViewChild('confirm')
	public confirmModal: ModalDirective<boolean>;

	private participantInfoTemplate: TextInterpolation;
	private participantInfo: string = null;
	private participantConfig: IParticipantConfig;
	public hasWorkflowWritePermission: boolean;
	public hasWorkflowWritePermission$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	finishWorkflowSplitbutton: SplitButtonElement = {
		title: 'workflow.end',
		type: 'primary',
		icon: 'check',
		callback: () => this.doCompletion(),
		disabled: true,
	};
	readonly workflowItem$: Observable<IQuestionnaireWorkflowItem>;
	readonly statusSet = new ReplaySubject<boolean>(1);
	hiddenStatus: boolean[] = [];
	questionHiddenStatus: boolean[][];
	questionStatus: string[][];

	protected constructor(
		injector: Injector,
		protected workflowItemDirective: WorkflowItemDirective = injector.get(WorkflowItemDirective),
		protected activeParticipantService: ActiveParticipantService = injector.get(ActiveParticipantService),
		protected route: ActivatedRoute = injector.get(ActivatedRoute),
		protected router: Router = injector.get(Router),
		protected workflowService: WorkflowService = injector.get(WorkflowService),
		protected workflowItemService: WorkflowItemNavigationService = injector.get(WorkflowItemNavigationService),
		protected workflowRestService: WorkflowRestService = injector.get(WorkflowRestService),
		protected projectService: ProjectService = injector.get(ProjectService),
		protected questionStatusService: QuestionStatusService = injector.get(QuestionStatusService),
		protected menuService: MenuService = injector.get(MenuService),
		protected interpolationService: InterpolationService = injector.get(InterpolationService),
		protected translateService: TranslateService = injector.get(TranslateService),
		protected store: Store = injector.get(Store),
		specialQuestionService: SpecialQuestionService = injector.get(SpecialQuestionService),
	) {
		this.individuelleFragetypen = specialQuestionService.getSpecialQuestionTypes();
		this.alleFrageTypen = ['BlockQuestion', ...this.individuelleFragetypen];


		this.workflowItem$ = this.workflowItemDirective.workflowItem$.pipe(
			map(workflowItem => workflowItem as IQuestionnaireWorkflowItem),
		);

	}

	ngOnInit(): void {

		this.commonSubscription.add(this.workflowItem$.pipe(
			tap(workflowItem => this.workflowItem = workflowItem),
			tap(workflowItem => this.isValidInput = workflowItem?.groups?.map(() => []) ?? []),
			switchMap(() => this.projectService.participantsConfig$),
			tap(config => this.loadParticipantDetailInfos(config)),
			switchMap(() => combineLatest([of(this.workflowItem), this.workflowItemDirective.current$, this.hasWorkflowWritePermission$])),
		).subscribe(([workflowItem, data]) => {
			this.updateHiddenStatus(workflowItem, data);
			this.updateValidity();
			this.updateSufficientStatusOfAll();
			this.updateButtons();
			this.updateConfirmationModal();
			this.updateGroupsStatus(workflowItem, data);
		}));

		this.commonSubscription.add(this.activeParticipantService.participant$.pipe(
			tap(participant => this.participant = participant),
			switchMap(() => this.projectService.participantsConfig$),
			tap(config => this.loadParticipantDetailInfos(config)),
		).subscribe());

		this.commonSubscription.add(this.workflowItemDirective.workflowStatus$.subscribe(status => {
			this.finishedAndLocked = !!status?.completed;
			this.updateButtons();
			this.statusSet.next(true);
		}));

		this.commonSubscription.add(this.workflowItemDirective.communicationStatus$.subscribe(status => {
			switch (status) {
				case CommunicationStatus.PENDING:
					this.finishWorkflowSplitbutton.icon = 'circle';
					break;
				default:
					this.finishWorkflowSplitbutton.icon = 'check';
			}
		}));

		this.commonSubscription.add(this.router.events
			.pipe(filter(event => event instanceof NavigationEnd))
			.subscribe(() => {
				this.loadParticipantDetailInfos();
			}));

		this.commonSubscription.add(
			this.projectService.hasWorkflowWriteOnActiveProject$
				.subscribe(
					hasWorkflowWritePermission => {
						this.hasWorkflowWritePermission = hasWorkflowWritePermission;
						this.hasWorkflowWritePermission$.next(this.hasWorkflowWritePermission);
					},
				),
		);
	}

	ngOnDestroy(): void {
		this.commonSubscription.unsubscribe();
		this.statusSet.complete();
	}

	/**
	 * Observable für Änderungen am itemId Route-Parameter
	 */
	protected get paramItemId$(): Observable<string> {
		return this.route.paramMap.pipe(
			switchMap(params => of(params.get('itemId'))),
			distinctUntilChanged(),
		);
	}

	/**
	 * All Questions are completed if there is not any obligatory question without any value
	 * @private
	 */
	private updateSufficientStatusOfAll(): void {
		this.unfinishedItems = {
			items: [], additionalAmount: 0,
		};
		const itemData = this.workflowItemDirective?.item;
		if (itemData && this.workflowItem) {
			const status = this.questionStatusService.checkWorkflowItemStatus(this.workflowItem, itemData);
			this.unfinishedItems.items = status.unfinished.map(group => group.title);
		}
		const isIncomplete = !!this.unfinishedItems.items.length;
		this.finishWorkflowSplitbutton.disabled = (!this.finishedAndLocked && (isIncomplete || !this.isAllValid || !this.hasWorkflowWritePermission));
		const limit = 3;
		if (this.unfinishedItems.items.length > limit) {
			this.unfinishedItems.additionalAmount = this.unfinishedItems.items.length - limit;
			this.unfinishedItems.items.splice(limit, this.unfinishedItems.additionalAmount);
		}
	}

	private updateConfirmationModal(): void {
		this.confirmationModalConfig = this.workflowItem?.config?.confDialog || null;
	}

	private updateButtons(): void {
		const endBtnString = this.workflowItem?.config?.primaryButtonLabel || 'workflow.end';
		this.finishWorkflowSplitbutton.title = this.finishedAndLocked ? 'workflow.endView' : endBtnString;
	}

	private updateHiddenStatus(workflowItem: IQuestionnaireWorkflowItem, data: SessionItemData) {
		const groups = workflowItem.groups || [];
		this.hiddenStatus = groups.map(group => !this.questionStatusService.checkGroupEnabled(group, data));

		this.questionHiddenStatus = groups.map(group => {
			return group.items.map(item => {
				return item.showIf && !data.getProperty(item.showIf);
			});
		});
	}

	private updateGroupsStatus(workflowItem: IQuestionnaireWorkflowItem, data: SessionItemData) {
		const groups = workflowItem.groups || [];

		this.questionStatus = groups.map(group => {
			const status = this.questionStatusService.checkQuestionGroupStatus(group, data, group.items);
			return status.blocks.map((block, index) => {
				const item = group.items[index];
				if (this.finishedAndLocked) {
					return 'check-circle';
				}
				if (item.hideCheckmarks) {
					return '';
				}
				return block.every(b => b === 'finished') ? 'check-circle' : '';
			});
		});
	}

	public updateInputValidity(valid: boolean, index: number, group: number): void {
		this.isValidInput[group][index] = valid;
		this.updateValidity();
	}
	
	public updateValidity(): void {
		this.isAllValid = this.isValidInput.every((groupValidity, gindex) => {
			const groupHidden = this.hiddenStatus?.[gindex];
			const itemsHidden = this.questionHiddenStatus?.[gindex] ?? [];
			if (groupHidden) {
				return true;
			}
			const groupValid = groupValidity.every((valid, qindex) => itemsHidden[qindex] || valid);
			return groupValid;
		});
		this.updateSufficientStatusOfAll();
	}

	showWorkflow(): void {
		this.router
			.navigate([this.workflowItemService.getFirstAvailableExitURL(this.workflowItem, EWorkflowPathKey.DETAIL, this.viewMode)])
			.catch(console.error);
	}

	showOverview(): void {
		this.router
			.navigate([this.workflowItemService.getFirstAvailableExitURL(this.workflowItem, EWorkflowPathKey.OVERVIEW, this.viewMode)])
			.catch(console.error);
	}

	async finishWorkflow(): Promise<void> {
		await this.workflowItemDirective.finishWorkflow();
		this.showWorkflow();
	}

	private getWorkflowStatus$(participant: IParticipant): Observable<WorkflowStatus> {
		return this.workflowRestService.getWorkflowStatus$(this.projectService.activeProject.project_id, participant.id);
	}

	protected loadParticipantDetailInfos(config?: IParticipantConfig): void {
		if (config) {
			this.participantConfig = config;
		}
		if (this.workflowItem && this.participant) {
			const template = this.participantConfig?.details?.info?.name;
			this.participantInfoTemplate = template && this.interpolationService.interpolation(template);
			this.participantInfo = this.participantInfoTemplate?.interpolate(this.participant.data) || this.participant.id;
			this.commonSubscription.add(this.translateService.stream('workflow.execute', { item: `${this.participantInfo} - ${this.workflowItem.name}` }).subscribe(
				result => this.menuService.title = result));
		}
	}

	public async doCompletion(): Promise<void> {
		if (this.finishedAndLocked) {
			this.showWorkflow();
			return;
		}
		
		try {
			if (this.workflowItem?.config?.disableConfirmDialog ||
				await this.confirmModal.show()) {
				await this.finishWorkflow();
			}
		} catch (e) {
			console.error(e);
		}
	}
}
